import { Immutable } from "immer";
import React, { useRef } from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { ProductFeatureBlock as Value } from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from
// `cms/home_page.html` (the `block_type == 'product_Feature_block'` branch)
// verbatim — every classname is load-bearing for the existing SCSS, so keep
// them in sync. The legacy template emitted TWO top-level sibling structures
// (a desktop `home-page-product-feature` block and a `home-page-product-feature-mobile`
// block); CSS shows/hides each by viewport. We return them as a fragment so the
// DOM structure matches (the page-level wrapper / scroll-trigger div is page
// chrome and is intentionally NOT part of this block).
type IProps = Immutable<{
    value: Value;
}>;

type Feature = NonNullable<Value["featureBlock"]>[number];

// Legacy zero-padded, 1-indexed counter: `0{{ forloop.counter }} ` (note the
// trailing space). `forloop.counter` is 1-indexed.
const featureNumber = (index: number): string => `0${index + 1} `;

// The legacy title div mixes a number-span child with the rich-text HTML
// (`{{ feature.title }}`, a RichText object rendered as raw HTML) as
// co-children. React forbids `dangerouslySetInnerHTML` alongside JSX children,
// so we build the inner HTML as a single string to preserve the exact element
// structure (no extra wrapper element). The number-span classname differs
// between desktop and mobile (mobile duplicates it — a legacy bug we reproduce
// verbatim for parity).
const titleInnerHTML = (
    feature: Immutable<Feature>,
    index: number,
    numberClassName: string,
): string =>
    `<span class="${numberClassName}">${featureNumber(index)}</span>${
        feature.title ?? ""
    }`;

const backgroundImageStyle = (
    image: Immutable<Feature>["image"],
): React.CSSProperties | undefined => {
    // Legacy emitted `style="background-image: url(<url>)"`. When the image is
    // null the legacy markup rendered `url()`; we instead omit the inline style
    // entirely while keeping the wrapper element (LifestyleModule precedent).
    if (!image) {
        return undefined;
    }
    return { backgroundImage: `url('${image.url}')` };
};

export const ProductFeatureBlock = (props: IProps): React.JSX.Element => {
    const features = props.value.featureBlock ?? [];

    // CAROUSEL INIT LIVES HERE (not in `client/src/ui/homepage.ts`): this component
    // owns the `.home-page-product-feature-mobile__container` DOM, so it owns its
    // own Flickity lifecycle via the shared `useFlickity` hook. The hook is
    // SSR-safe -- it dynamic-imports flickity inside a client-only effect (a
    // TOP-LEVEL import would crash the SSR renderer, since flickity's UMD factory
    // references `window` at evaluation time) and tears down with `safeDestroy`.
    // The `> 1` gate is a deliberate (minor) delta from legacy, which inited the
    // FIRST mobile container unconditionally via `querySelector`; gating on a real
    // multi-slide carousel skips pointless single-cell init. Options match the
    // legacy homepage.ts call.
    const mobileContainerRef = useRef<HTMLDivElement>(null);
    useFlickity(mobileContainerRef, {
        options: { watchCSS: true, prevNextButtons: false, pageDots: false },
        enabled: features.length > 1,
        deps: [features.length],
    });

    return (
        <>
            <div
                className="l-capped-width home-page-product-feature"
                tabIndex={0}
                style={{ overflowY: "auto" }}
            >
                <div className="home-page-product-feature__container">
                    <hr className="home-page-product-feature__border home-page-product-feature__border--top" />
                    {features.length > 0 && (
                        <>
                            <div className="home-page-product-feature__col home-page-product-feature__copy">
                                {features.map((feature, index) => (
                                    <div
                                        key={index}
                                        className="home-page-product-feature__content home-page-product-feature__inner"
                                    >
                                        <div
                                            className="home-page-product-feature__title home-page-section__title"
                                            dangerouslySetInnerHTML={{
                                                __html: titleInnerHTML(
                                                    feature,
                                                    index,
                                                    "home-page-product-feature__title--number",
                                                ),
                                            }}
                                        />
                                        <div className="home-page-product-feature__desc home-page-section__desc">
                                            {feature.description}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="home-page-product-feature__col home-page-product-feature__image-list">
                                {/* Legacy looped `featureBlock reversed`. Copy
                                    the array before reversing — the prop is
                                    immutable and shared. */}
                                {[...features]
                                    .reverse()
                                    .map((feature, index) => (
                                        <div
                                            key={index}
                                            className="home-page-product-feature__image home-page-product-feature__inner"
                                            style={backgroundImageStyle(
                                                feature.image,
                                            )}
                                        ></div>
                                    ))}
                            </div>
                        </>
                    )}
                    <hr className="home-page-product-feature__border home-page-product-feature__border--bottom" />
                </div>
            </div>

            <div className="home-page-product-feature-mobile">
                <div className="home-page-product-feature__border home-page-product-feature__border--mobile"></div>
                <div
                    className="home-page-product-feature-mobile__container"
                    ref={mobileContainerRef}
                >
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="home-page-product-feature-mobile__inner"
                        >
                            <div className="home-page-product-feature-mobile__image">
                                {/* Legacy used `<img src="{{ feature.image.file.url }}">`.
                                    When the image is null the legacy rendered an
                                    empty src; we omit the src attribute. */}
                                <img
                                    className="responsive-img"
                                    alt=""
                                    src={feature.image?.url ?? undefined}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div
                                className="home-page-product-feature-mobile__title home-page-section__title"
                                dangerouslySetInnerHTML={{
                                    __html: titleInnerHTML(
                                        feature,
                                        index,
                                        // Legacy duplicated this classname in
                                        // the mobile markup — preserved verbatim.
                                        "home-page-product-feature__title--number home-page-product-feature__title--number",
                                    ),
                                }}
                            />
                            <div className="home-page-product-feature-mobile__desc home-page-section__desc">
                                {feature.description}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="home-page-product-feature__border home-page-product-feature__border--mobile home-page-product-feature__border--mobile-bottom"></div>
            </div>
        </>
    );
};
